import { useEffect, useMemo, useRef, useState } from 'react'

import { debounce } from 'lodash'

import {
  Search,
  Separator,
  Spotlight,
  SpotlightContent,
  SpotlightEmpty,
  SpotlightFooter,
  SpotlightGroup,
  SpotlightInput,
  SpotlightList,
  SpotlightPanel,
  SpotlightTrigger,
  Trackable,
} from '@forto-dev/phoenix-ui-core'

import { useSearchlight } from '@/features/searchlight/api/useSearchlight'
import { SearchlightResultItem } from '@/features/searchlight/components/searchlightResultItem'
import { useRecentSearches } from '@/features/searchlight/hooks/useRecentSearches'
import { groupResults } from '@/features/searchlight/utils'
import { useLocalisation } from '@/utils/localisation'

import { PageItem } from './pageItem'

const SHORTCUT_KEY = 'K'

type SearchlightSearchProps = {
  testId?: string
}

export const SearchlightSearch = ({ testId }: SearchlightSearchProps) => {
  const { translate } = useLocalisation()
  const searchTriggerRef = useRef<HTMLButtonElement>(null)
  const [searchText, setSearchText] = useState<string>('')
  const debouncedHandleSearch = useMemo(() => debounce((text) => setSearchText(text), 1000), [])
  const { recentResults, addResult } = useRecentSearches()
  const result = useSearchlight(searchText)
  const groups = groupResults(result.data ?? [])

  const handleSearch = async (text: string) => {
    debouncedHandleSearch(text)
  }

  useEffect(() => {
    const onKeyPressHandler = (event: KeyboardEvent) => {
      const path = event.composedPath()
      const isInput = path.some(
        (element) => element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
      )
      const isMac = navigator.userAgent.includes('Mac')
      const isModifierPressed = isMac ? event.metaKey : event.ctrlKey
      if (!isInput && isModifierPressed && event.key.toUpperCase() === SHORTCUT_KEY) {
        event.preventDefault()

        searchTriggerRef.current?.click()
      }
    }

    document.addEventListener('keydown', onKeyPressHandler)

    return () => {
      document.removeEventListener('keydown', onKeyPressHandler)
    }
  }, [])

  return (
    <Spotlight>
      <SpotlightTrigger asChild>
        <Trackable data-testid={testId}>
          <Search
            ref={searchTriggerRef}
            label={translate('search.placeholder')}
            shortcut={`${navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl'}+${SHORTCUT_KEY}`}
          />
        </Trackable>
      </SpotlightTrigger>
      <SpotlightContent>
        <SpotlightPanel>
          <Trackable data-testid="searchlight-input">
            <SpotlightInput placeholder={translate('search.placeholder')} onValueChange={handleSearch} />
          </Trackable>
          <Trackable data-testid="searchlight-list">
            <SpotlightList>
              {result.isFetching ? (
                <SpotlightEmpty>{translate('search.loading')}</SpotlightEmpty>
              ) : (
                <Trackable data-testid="searchlight-list-empty">
                  <SpotlightEmpty>{translate('search.noResultsFound')}</SpotlightEmpty>
                </Trackable>
              )}

              {!searchText && recentResults.length ? (
                <SpotlightGroup key="recent" heading={translate('search.groups.recent')}>
                  {recentResults.map((result, key) => (
                    <SearchlightResultItem
                      key={result.entityID + key}
                      result={result}
                      onSelect={() => {
                        addResult(result)
                      }}
                    />
                  ))}
                </SpotlightGroup>
              ) : null}

              <SpotlightGroup key="pages" heading={translate('search.groups.pages')}>
                <PageItem title={translate('navigation.overview')} link="/" />
                <PageItem title={translate('navigation.quotes')} link="/quotes" />
                <PageItem title={translate('navigation.billing')} link="/billing" />
              </SpotlightGroup>

              {groups.map((group) => (
                <SpotlightGroup key={group.title} heading={translate(group.title)}>
                  {group.results.map((result, key) => (
                    <SearchlightResultItem
                      key={result.entityID + key}
                      result={result}
                      onSelect={() => {
                        addResult(result)
                      }}
                    />
                  ))}
                </SpotlightGroup>
              ))}
            </SpotlightList>
          </Trackable>
          <SpotlightFooter>
            {translate('search.footer.navigate')}
            <Separator orientation="vertical" />
            {translate('search.footer.select')}
            <Separator orientation="vertical" />
            {translate('search.footer.close')}
          </SpotlightFooter>
        </SpotlightPanel>
      </SpotlightContent>
    </Spotlight>
  )
}
